import { useEffect, useState } from 'react';
import { encodeFunctionData } from 'viem';
import { ethers } from 'ethers';
import axios from 'axios';

import { abiChestNFT, abiChestFactory } from '../config/abi.contract';
import useWrappedRPC from './useWrappedRPC';
import useEmbeddedWallet from './useEmbeddedWallet';
import {
  getChestMintSignature,
  validateOpenChestTransaction,
  validateWithdrawChestTransaction,
} from '../services/transaction.service';
import { getLuckyChestConfig } from '../services/chest.service';
import useToken from './useToken';
import { dispatchGameEvent } from '../utils/common';
import environments from '../utils/environments';

const { PUBLIC_RPC_URL, CHEST_NFT_CONTRACT_ADDRESS, CHEST_FACTORY_CONTRACT_ADDRESS, CHAIN_ID } = environments;

function useChest() {
  const { getXFISHTokenBalance } = useToken();
  const { WrappedJsonProvider, sendTransactionWithGas, customChainNetwork } = useWrappedRPC();
  const { getEmbeddedWallet } = useEmbeddedWallet();

  // State
  const [luckyChestConfig, setLuckyChestConfig] = useState(null);
  const [luckyChestRewardsDropRates, setLuckyChestRewardsDropRates] = useState(null);
  const [luckyChestTotalPercentage, setLuckyChestTotalPercentage] = useState(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);

  // Contracts
  const provider = new WrappedJsonProvider(PUBLIC_RPC_URL, customChainNetwork);
  const erc721Contract = new ethers.Contract(CHEST_NFT_CONTRACT_ADDRESS, abiChestNFT, provider);
  const chestFactoryContract = new ethers.Contract(CHEST_FACTORY_CONTRACT_ADDRESS, abiChestFactory, provider);

  // ---- Fetch Lucky Chest Configuration ---- //
  useEffect(() => {
    const fetchLuckyChestConfig = async () => {
      try {
        setIsLoadingConfig(true);
        const response = await getLuckyChestConfig();
        console.log('✅ Lucky chest config loaded:', response.data);
        setLuckyChestConfig(response.data.config);
      } catch (error) {
        console.error('❌ Error fetching lucky chest config:', error);
      } finally {
        setIsLoadingConfig(false);
      }
    };

    fetchLuckyChestConfig();
  }, []);

  useEffect(() => {
    fetchTokenRewardPercentages();
  }, []);

  // ---- Fetch Reward Drop Rates from Contract ---- //
  const fetchTokenRewardPercentages = async () => {
    const rewardRates = [];
    let index = 0;

    // Fetch all reward tiers until we hit an empty one
    while (true) {
      try {
        const { percentage, amount } = await chestFactoryContract.tokenRewardPercentages(index);
        const totalPercentage = await chestFactoryContract.totalPercentage();

        setLuckyChestTotalPercentage(totalPercentage.toNumber()); // e.g. 10000
        // Stop when we hit a tier with 0 percentage
        if (percentage.isZero()) {
          break;
        }

        const formattedRate = {
          percentage: percentage.toNumber(),
          amount: parseFloat(ethers.utils.formatEther(amount)),
        };

        console.log(`💰 Reward tier ${index}:`, formattedRate);
        rewardRates.push(formattedRate);
        index++;
      } catch (error) {
        console.error('❌ Error fetching reward percentages:', error);
        break;
      }
    }

    console.log('✅ All reward tiers loaded:', rewardRates);
    setLuckyChestRewardsDropRates(rewardRates);

    return rewardRates;
  };

  // ---- Get Lucky Chest Rewards by Request Hash ---- //
  const getLuckyChestRewardsByReqHash = async ({ requestHash, txnHash }, maxRetries = MAX_RETRIES) => {
    let attempts = 0;

    while (attempts < maxRetries) {
      attempts++;

      try {
        // Try to get result from contract
        const result = await chestFactoryContract.requestContexts(requestHash);

        // Check if we have a valid result with non-zero amount
        if (result?.amount && !result.amount.isZero()) {
          console.log('✅ Lucky chest reward found:', {
            user: result.user,
            amount: parseFloat(ethers.utils.formatEther(result.amount)),
            requestHash,
          });

          return {
            ...result,
            amount: parseFloat(ethers.utils.formatEther(result.amount)),
            fromVRF: false,
          };
        }

        // Fallback to VRF API if user address is zero (not yet fulfilled)
        if (result?.user === ethers.constants.AddressZero) {
          console.log('🔄 Request not fulfilled yet, checking VRF API...');

          try {
            const response = await axios.get(`https://staging.vrf.proofofplay.com/v1/vrf/${CHAIN_ID}/${txnHash}`);

            if (response.data?.randomNumber) {
              console.log('🎲 Random number from VRF:', response.data.randomNumber);

              if (!luckyChestRewardsDropRates) await fetchTokenRewardPercentages();

              const rewardAmount = calculateRewardFromRandomNumber(
                response.data.randomNumber,
                luckyChestTotalPercentage,
                luckyChestRewardsDropRates
              );

              console.log('✅ Reward calculated from VRF:', rewardAmount);

              return {
                user: result.user,
                amount: rewardAmount,
                requestHash,
                fromVRF: true,
              };
            }
          } catch (apiError) {
            console.warn('⚠️ VRF API call failed:', apiError.message);
          }
        }
      } catch (error) {
        console.error(`❌ Error on attempt ${attempts}:`, error.message);
      }

      // Wait before retry
      if (attempts < maxRetries) {
        console.log(`⏳ Attempt ${attempts}/${maxRetries} - Retrying in ${RETRY_DELAY_MS}ms...`);
        await delay(RETRY_DELAY_MS);
      }
    }

    throw new Error('Timeout waiting for lucky chest rewards. Please check back later or contact support.');
  };

  // ---- Ensure NFT Approval for Chest Factory ---- //
  const ensureChestApproval = async (userAddress) => {
    const isApproved = await erc721Contract.isApprovedForAll(userAddress, CHEST_FACTORY_CONTRACT_ADDRESS);

    if (isApproved) {
      console.log('✅ Chest approval already granted');
      return;
    }

    console.log('🔐 Requesting chest approval...');

    const approvalData = encodeFunctionData({
      abi: abiChestNFT,
      functionName: 'setApprovalForAll',
      args: [CHEST_FACTORY_CONTRACT_ADDRESS, true],
    });

    await sendTransactionWithGas(
      {
        to: CHEST_NFT_CONTRACT_ADDRESS,
        data: approvalData,
      },
      {
        header: 'Approve all chests',
        description: 'Approve all chests for Fishing Game',
        buttonText: 'Approve all',
      }
    );

    console.log('✅ Chest approval granted');
  };

  // ---- Open Lucky Chest ---- //
  const openLuckyChest = async (tokenId, callback) => {
    try {
      console.log('🎁 Opening lucky chest:', tokenId);

      const embeddedWallet = await getEmbeddedWallet();

      // Ensure approval
      await ensureChestApproval(embeddedWallet.address);

      // Get VRF fee
      const vrfFee = await chestFactoryContract.vRNGFee();
      console.log('💸 VRF fee:', ethers.utils.formatEther(vrfFee), 'ETH');

      // Encode transaction data
      const data = encodeFunctionData({
        abi: abiChestFactory,
        functionName: 'openLuckyChest',
        args: [tokenId],
      });

      const txUiConfig = {
        header: 'Open Lucky Chest',
        description: `Open chest with ID ${tokenId}`,
        buttonText: 'Open',
      };

      const transactionRequest = {
        to: CHEST_FACTORY_CONTRACT_ADDRESS,
        value: vrfFee,
        data,
      };

      // Send transaction
      const txReceipt = await sendTransactionWithGas(transactionRequest, txUiConfig);

      if (!txReceipt?.hash) {
        throw new Error('Transaction failed on blockchain');
      }

      console.log('✅ Transaction confirmed:', txReceipt.hash);

      // Notify game to refresh inventory
      dispatchGameEvent({
        type: 'refreshInventoryChest',
        hasWaiting: false,
        notifyMessage: null,
      });

      if (callback) callback();

      // Get transaction receipt and extract request ID
      const receipt = await provider.getTransactionReceipt(txReceipt.hash);

      // RequestId is the 2nd indexed parameter of the RandomNumberRequested event (3rd log)
      const requestId = receipt.logs[2]?.topics[1];

      if (!requestId) {
        throw new Error('Could not extract request ID from transaction receipt');
      }

      console.log('🎲 Request ID:', requestId);

      // Wait for and get rewards
      const rewardResult = await getLuckyChestRewardsByReqHash({
        requestHash: requestId,
        txnHash: txReceipt.hash,
      });

      console.log('💰 Reward amount:', rewardResult.amount, 'xFISH');

      // Format reward data with animation config
      const rewardsData = formatRewardData(rewardResult.amount, luckyChestConfig);

      // Get updated balance
      const updatedBalance = await getXFISHTokenBalance(embeddedWallet.address);
      console.log('💵 Updated xFISH balance:', updatedBalance.toLocaleString());

      // Final inventory refresh
      dispatchGameEvent({
        type: 'refreshInventoryChest',
        hasWaiting: false,
        notifyMessage: null,
      });

      return {
        success: true,
        message: 'Lucky chest opened successfully',
        data: {
          ...rewardsData,
          xFishTokenBalance: updatedBalance,
          fromVRF: rewardResult.fromVRF,
        },
      };
    } catch (error) {
      console.error('❌ Open lucky chest failed:', error);
      return {
        success: false,
        message: error.message || 'Failed to open lucky chest',
      };
    }
  };

  // ---- Open Batch NFT Chests ---- //
  const openBatchNFTChest = async (userChestTokenIds, callback) => {
    try {
      console.log('🎁 Opening batch chests:', userChestTokenIds);

      const embeddedWallet = await getEmbeddedWallet();

      // Ensure approval
      await ensureChestApproval(embeddedWallet.address);

      // Encode transaction data
      const data = encodeFunctionData({
        abi: abiChestFactory,
        functionName: 'openBatchChest',
        args: [userChestTokenIds],
      });

      const txUiConfig = {
        header: 'Open Chests',
        description: `Open chests with IDs ${userChestTokenIds.join(', ')}`,
        buttonText: 'Open',
      };

      const transactionRequest = {
        to: CHEST_FACTORY_CONTRACT_ADDRESS,
        data,
      };

      // Send transaction
      const txReceipt = await sendTransactionWithGas(transactionRequest, txUiConfig);

      if (!txReceipt?.hash) {
        throw new Error('Transaction failed on blockchain');
      }

      console.log('✅ Batch open transaction confirmed:', txReceipt.hash);

      if (callback) callback();

      // Validate with backend
      const backendResponse = await validateOpenChestTransaction({
        transactionHash: txReceipt.hash,
      });

      return {
        success: true,
        message: 'Chests opened successfully',
        data: backendResponse.data,
      };
    } catch (error) {
      console.error('❌ Open batch chests failed:', error);
      return {
        success: false,
        message: error.message || 'Failed to open chests',
      };
    }
  };

  // ---- Withdraw/Mint Chests ---- //
  const withdrawChest = async (userChestTokenIds) => {
    try {
      console.log('📤 Withdrawing chests:', userChestTokenIds);

      await getEmbeddedWallet();

      // Get signature from backend
      const signatureRes = await getChestMintSignature({ userChestTokenIds });
      const { chestTokenIds, tierIds, quantities, nonce, deadline, signature } = signatureRes.data;

      // Encode transaction data
      const data = encodeFunctionData({
        abi: abiChestFactory,
        functionName: 'mintBatchChestWithSignature',
        args: [chestTokenIds, tierIds, quantities, nonce, deadline, signature],
      });

      const txUiConfig = {
        header: 'Withdraw Chest',
        description: 'Withdraw Chest',
        buttonText: 'Withdraw',
      };

      const transactionRequest = {
        to: CHEST_FACTORY_CONTRACT_ADDRESS,
        data,
      };

      // Send transaction
      const tx = await sendTransactionWithGas(transactionRequest, txUiConfig);

      if (!tx?.hash) {
        throw new Error('Transaction failed on blockchain');
      }

      console.log('✅ Withdraw transaction confirmed:', tx.hash);

      // Validate with backend
      const backendResponse = await validateWithdrawChestTransaction({
        transactionHash: tx.hash,
      });

      return {
        success: true,
        message: 'Chests withdrawn successfully',
        data: backendResponse.data,
      };
    } catch (error) {
      console.error('❌ Withdraw chests failed:', error);
      return {
        success: false,
        message: error.message || 'Failed to withdraw chests',
      };
    }
  };

  return {
    openLuckyChest,
    openBatchNFTChest,
    withdrawChest,
  };
}

export default useChest;

// utils

// ---- Constants ---- //
const MAX_RETRIES = 30; // 30 seconds with 1s delay
const RETRY_DELAY_MS = 1000;
const BASIS_POINTS_DIVISOR = 10000; // 100.00% = 10000 basis points

// ---- Helper functions ---- //
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Mirrors ChestFactory contract's _getReward logic
 * @param {string|number} randomNumber - Random number from VRF
 * @param {number} totalPercentage - Total percentage in basis points
 * @param {Array} rewardRates - Array of {percentage, amount} objects
 * @returns {number} Reward amount (formatted as number, not wei)
 */
const calculateRewardFromRandomNumber = (randomNumber, totalPercentage, rewardRates) => {
  console.log({ randomNumber, totalPercentage, rewardRates });
  if (!totalPercentage || totalPercentage === 0 || !rewardRates?.length) {
    return 0;
  }

  // Convert BigNumber to number if needed
  const randomNum = typeof randomNumber === 'string' ? ethers.BigNumber.from(randomNumber) : randomNumber;

  // Get roll value (0-9999 for 100.00%)
  const roll = randomNum.mod(BASIS_POINTS_DIVISOR).toNumber();

  console.log('roll', roll);

  // If roll is outside total percentage range, no reward
  if (roll >= totalPercentage) {
    return 0;
  }

  // Find matching reward tier
  let cumulative = 0;
  for (const tier of rewardRates) {
    cumulative += tier.percentage;
    if (roll < cumulative) {
      return tier.amount;
    }
  }

  return 0;
};

/**
 * Format reward data for display with animation config
 * @param {number} amount - Reward amount
 * @param {Object} luckyChestConfig - Configuration from backend
 * @returns {Object} Formatted reward data
 */
const formatRewardData = (amount, luckyChestConfig) => {
  const defaultReward = {
    imageName: 'xfish_token',
    name: 'xFISH',
    type: 'xFish Token',
    description: 'Token that can be used for in-game utility or converted to FISH.',
    quantity: amount,
    quality: 1,
    order: 1,
    animation: 1,
  };

  if (!luckyChestConfig?.lootItems || amount === 0) {
    return { totalItems: [defaultReward] };
  }

  const matchingReward = luckyChestConfig.lootItems[0].find((item) => item.quantity === amount);

  return {
    totalItems: [matchingReward || defaultReward],
  };
};
